import React, { Component } from 'react';
import { ColumnProps } from 'antd/lib/table';
import { debounce, isEmpty, isEqual, isNil, isUndefined, mapValues } from 'lodash-es';

import { Spin, Table } from 'components/antd';
import { EditableFormRow } from './TableRow';
import { EditableCell } from './TableCell';
import TableCellHeader from './TableCellHeader';
import DeleteBtn from './DeleteBtn';
import classNames from 'classnames';
import styles from './TableBuilder.module.scss';
import scrollTableStyles from './ScrollTable.module.scss';
import { TABLE_SIZE_CONFIG } from 'app-constants';

export interface TableBuilderColumnProps<T = any> extends ColumnProps<T> {
	titleText?: string;

	// This param should be the same as API param for filtering
	// for this column (check API in swagger)
	searchParam?: string;

	// This param should be the same as property name in data object
	// It is in the swagger in responses structure.
	// type "keyof T | string | any" is only for IDE autocomplete.
	dataIndex?: keyof T | string | any;

	// This param should be the same as API param for adding
	// for this column (check API in swagger)
	addParam?: string;

	// This param should be the same as API param for sorting
	// for this column. It is certain enum for each column.
	// It is not in the swagger, you should ask developers for that.
	orderColumn?: string;

	// are cells of this table editable
	editable?: boolean;

	// is this column required due adding new entity
	required?: boolean;

	// is this header column should be Select due filtering or adding
	isSelect?: boolean;

	// is this header column should be Checkbox due filtering or adding
	isCheckbox?: boolean;

	// checkbox label
	checkboxLabel?: string;

	// is this header column should be InputNumber due filtering or adding
	isNumberValue?: boolean;

	// it is set for filtering columns only. It means start filtering from the first letter
	//(by default it starts from 3rd)
	shortSearchText?: boolean;
}

interface TableBuilderProps {
	columns: TableBuilderColumnProps[];

	// total number of items for pagination
	dataTotal: number;

	isLoading: boolean;

	// initial column for sorting
	initialOrderColumn: string;

	addRowTitle?: string;

	// deleteId Name which is used for deletion and certain for each table
	deleteIdName?: string;

	// if Slects are used in header cells it's data for such select
	selectsData?: any;

	// for cases with external filters to handle pagination manually
	customCurrentPage?: number;

	data: any;
	onRetrieveData: any;
	onUpdateData?: any;
	onAddData?: any;
	onDeleteData?: any;
	orderByAscending?: boolean;
	numberOfItemsOnPageOnInit?: typeof TABLE_SIZE_CONFIG[number];

	// for tables with fixed columns
	scroll?: object;

	setRowClassNames?: (rowIndex: number) => string;
}
interface TableBuilderState {
	pageConfig: any;
	isClearBtnVisible?: boolean;
}

class TableBuilder extends Component<TableBuilderProps, TableBuilderState> {
	prevSorter: any;

	constructor(props: TableBuilderProps) {
		super(props);

		this.prevSorter = undefined;
		const { orderByAscending, numberOfItemsOnPageOnInit } = props;
		this.state = {
			pageConfig: {
				'page.NumberOfItemsOnPage': numberOfItemsOnPageOnInit || 10,
				'page.PageNumber': 1,
				'order.OrderByAscending': orderByAscending === undefined ? true : orderByAscending,
				'order.OrderColumn': this.props.initialOrderColumn,
			},
		};
	}

	onRetrieveData = debounce(v => this.props.onRetrieveData(v), 500);

	retrieveData(pageConfig?: any, noNeedRetrieveData?: boolean) {
		if (pageConfig) {
			this.setState({
				pageConfig,
				isClearBtnVisible: this.checkIsClearBtnVisible(pageConfig),
			});
		}
		if (!noNeedRetrieveData) {
			this.onRetrieveData(this.getFilteringPageConfig(pageConfig || this.state.pageConfig));
		}
	}

	getFilteringPageConfig(pageConfig: any) {
		// GROS-1192
		const { columns } = this.props;
		const newPageConfig = mapValues(pageConfig, (value: string, key: string) => {
			let searchingValue: string | undefined = value;
			columns.forEach((col: any) => {
				if (col.searchParam === key && value) {
					if (col.shortSearchText && value.length < 1) {
						searchingValue = undefined;
					} else if (!col.shortSearchText && value.length < 3) {
						searchingValue = undefined;
					}
				}
			});

			return searchingValue;
		});
		return newPageConfig;
	}

	checkIsClearBtnVisible = (pageConfig: any) => {
		const { columns } = this.props;
		return columns.some(({ searchParam }: any) => {
			return (
				!isNil(pageConfig[searchParam]) &&
				pageConfig[searchParam] !== '' &&
				pageConfig[searchParam] !== false
			);
		});
	};

	componentDidMount() {
		this.props.onRetrieveData(this.getFilteringPageConfig(this.state.pageConfig));
	}

	handleSearch = (searchParams: any, noNeedRetrieveData?: boolean) => {
		this.retrieveData(
			{
				...this.state.pageConfig,
				'page.PageNumber': 1,
				...searchParams,
			},
			noNeedRetrieveData
		);
	};

	handleSave = (updatedCountry: any) => {
		this.props.onUpdateData({
			...updatedCountry,
			pagedRequest: this.state.pageConfig,
		});
	};

	handleAdd = (addParams: any) => {
		this.props.onAddData({
			...addParams,
			pagedRequest: this.state.pageConfig,
		});

		// TODO: refactor it to rid of this hardcode
		if (addParams.countryId) {
			this.setState({
				pageConfig: {
					...this.state.pageConfig,
					countryId: addParams.countryId,
				},
			});
		}
	};

	clearFilters = () => {
		const { pageConfig } = this.state;
		this.retrieveData({
			'page.NumberOfItemsOnPage': pageConfig['page.NumberOfItemsOnPage'],
			'page.PageNumber': pageConfig['page.PageNumber'],
			'order.OrderByAscending': pageConfig['order.OrderByAscending'],
			'order.OrderColumn': pageConfig['order.OrderColumn'],
		});
	};

	onPageChange = (current?: number, size?: number) => {
		// handel pagination
		let pageNumber = current || this.state.pageConfig['page.PageNumber'];

		// reset to first page on changing page size
		if (size !== this.state.pageConfig['page.NumberOfItemsOnPage']) {
			pageNumber = 1;
		}

		const pageConfig = {
			...this.state.pageConfig,
			'page.PageNumber': pageNumber,
			'page.NumberOfItemsOnPage': size || this.state.pageConfig['page.NumberOfItemsOnPage'],
		};

		this.retrieveData(pageConfig);
	};

	isSortAfterDefault = (sorter: any) => {
		// 1 - first sorting and it is not default sorted column
		// 2 - not first sorting, but sorting column was changed
		return (
			(isUndefined(this.prevSorter) &&
				sorter.column.orderColumn !== this.props.initialOrderColumn) ||
			(!isUndefined(this.prevSorter) &&
				this.prevSorter.column &&
				!isEmpty(sorter) &&
				sorter.column.orderColumn !== this.prevSorter.column.orderColumn)
		);
	};

	onTableChange = (_pagination: any, _filters: any, sorter: any) => {
		// handle sorting
		const { pageConfig } = this.state;

		const isPaginationChange = isUndefined(this.prevSorter) && isEmpty(sorter);

		if (!isPaginationChange && !isEqual(sorter, this.prevSorter)) {
			const orderColumn =
				sorter && sorter.column
					? sorter.column.orderColumn
					: pageConfig['order.OrderColumn'];
			let orderByAscending;

			if (this.isSortAfterDefault(sorter)) {
				orderByAscending = true;
			} else {
				orderByAscending = !pageConfig['order.OrderByAscending'];
			}

			this.prevSorter = sorter;

			this.retrieveData({
				...this.state.pageConfig,
				'page.PageNumber': 1,
				'order.OrderByAscending': orderByAscending,
				'order.OrderColumn': orderColumn,
			});
		}
	};

	onPageSizeChange = (item: number) => {
		this.onPageChange(undefined, item);
	};

	onDeleteData = (itemId: string) => {
		const { data } = this.props;
		const { pageConfig } = this.state;
		let resultPageConfig = { ...pageConfig };

		if (data.length === 1 && pageConfig['page.PageNumber'] > 1) {
			resultPageConfig['page.PageNumber'] = pageConfig['page.PageNumber'] - 1;

			this.setState({
				pageConfig: resultPageConfig,
			});
		}

		this.props.onDeleteData(resultPageConfig, itemId);
	};

	getColumns = (columns: any) => {
		// TODO: move to separate file
		const { scroll, isLoading } = this.props;
		const { pageConfig, isClearBtnVisible } = this.state;

		return columns.map((col: any, index: number) => {
			const isLastCol = index === columns.length - 1;
			const title = (
				<TableCellHeader
					col={col}
					isLoading={isLoading}
					isLastCol={isLastCol}
					pageConfig={pageConfig}
					handleSearch={this.handleSearch}
					clearFilters={this.clearFilters}
					isClearBtnVisible={isClearBtnVisible}
					selectsData={this.props.selectsData}
					scroll={scroll}
				/>
			);

			let newCol = {
				...col,
				title,
			} as TableBuilderColumnProps;

			if (col.editable) {
				newCol = {
					...newCol,
					onCell: (record: any) => ({
						record,
						col: col,
						handleSave: this.handleSave,
					}),
				};
			}

			if (col.key === 'delete') {
				const hasOwnRender = col.render && col.render('', {}) !== undefined;

				if (!hasOwnRender) {
					col.render = (_text: string, record: any) => (
						<DeleteBtn
							itemId={record[this.props.deleteIdName || '']}
							onDelete={this.onDeleteData}
						/>
					);
				}
			}

			return newCol;
		});
	};

	isEditableTable() {
		const { columns } = this.props;

		return columns.filter((col: any) => col.editable).length > 0;
	}

	render() {
		const { pageConfig } = this.state;
		const {
			data,
			dataTotal,
			isLoading,
			columns,
			addRowTitle,
			scroll,
			customCurrentPage,
			setRowClassNames,
		} = this.props;
		const components = {
			body: {
				row: EditableFormRow,
				cell: EditableCell,
			},
		};

		const isEmptyRows = data.length === 0;

		return (
			<Spin spinning={isLoading}>
				<Table
					onRow={(_record, rowIndex) => ({
						index: rowIndex,
						columns: columns,
						isEmptyRows,
						addRowTitle: addRowTitle,
						handleAdd: this.handleAdd,
						scroll: scroll,
						setRowClassNames,
					})}
					components={components}
					columns={this.getColumns(columns)}
					dataSource={isEmptyRows ? [{}] : data}
					rowKey={(_record, index) => `dictionaryRow-${index}`}
					onChange={this.onTableChange}
					activeSizeItem={pageConfig['page.NumberOfItemsOnPage']}
					onPageSizeChange={this.onPageSizeChange}
					scroll={scroll}
					pagination={
						!isEmptyRows && {
							current: customCurrentPage || pageConfig['page.PageNumber'],
							pageSize: pageConfig['page.NumberOfItemsOnPage'],
							total: dataTotal,
							onChange: this.onPageChange,
							size: 'small',
						}
					}
					className={classNames(
						this.isEditableTable() ? styles.editableTable : styles.notEditableTable,
						{ [scrollTableStyles.scollTable]: scroll }
					)}
				/>
			</Spin>
		);
	}
}

export default TableBuilder;
